/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */

import _ from 'lodash';
import React from 'react';

import PropTypes from 'prop-types';

import {
  Icon,
  ListView,
  ListViewAction,
  PopupListView,
} from '@mairistem/composants';
import { Event, View } from '@mairistem/planning';

import ListViewPictos from './ListViewPictos';

import {
  COULEUR_BLEU_JVS,
} from '../../const/colors';
import {
  ParametreContext,
  PlanningContext,
  ReservantContext,
  ReservationContext,
  SalleContext,
} from '../../context';
import { getReservationColor, getReservationTooltipText } from '../../utils';

const Creneau = (props) => {
  const {
    dateDebut: debutCreneau,
    reservations,
    partiel,
    ...rest
  } = props;

  const { salles } = React.useContext(SalleContext);
  const { reservants } = React.useContext(ReservantContext);
  const { statuts, typesReservation } = React.useContext(ReservationContext);
  const {
    view,
    reservationHandlers: { create, goTo },
  } = React.useContext(PlanningContext);
  const { typeCouleur, couleurParticulier } = React.useContext(ParametreContext);

  // Clic sur le bouton "Ajouter une réservation" de la popup
  const handleCreate = React.useCallback((e) => {
    const salle = _.find(
      salles,
      { identifiant: _.head(reservations)?.identifiantResource },
    );

    create(e, '', salle, true);
  }, [salles, reservations]);

  // Clic sur le titre d'une réservation pour aller sur sa fiche Webdev
  const handleRedirect = React.useCallback((e, reservation) => {
    goTo(e, reservation);
  }, [goTo]);

  const handleEventClick = React.useCallback((e) => {
    // Si on a qu'une réservation
    // Qu'elle n'est pas partielle OU qu'elle est partielle et qu'on est en vu jour
    // → On va sur le détail
    if (reservations.length === 1 && (!partiel || (view === View.DAY && partiel))) {
      goTo(e, _.head(reservations));
    }
  }, [reservations, goTo, view]);

  const handlePopupClick = React.useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Détermine la position de la popup en fonction de la position de l'élément dans le viewport
  const [popupPosition, setPopupPosition] = React.useState('right center');
  const [popupOffset, setPopupOffset] = React.useState([0, 0]);
  const eventRef = React.useRef(null);

  const handleMouseEnter = React.useCallback(() => {
    if (eventRef.current) {
      const rect = eventRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const middleOfViewport = viewportWidth / 2;

      // Si l'élément est dans la partie gauche, afficher la popup à droite
      // Sinon, afficher la popup à gauche
      setPopupPosition(rect.left < middleOfViewport ? 'right center' : 'left center');

      // Déplace la popup loin de ou vers l'élément de référence (perpendiculairement)
      const elementHeight = rect.height;
      const distance = -(elementHeight / 2) + 10;

      // Déplace la popup le long de l'élément de référence (parallèlement)
      // Au delà de 2 réservations on ne décale plus
      const nbReservations = reservations.length;

      let skidding = 0;

      if (nbReservations === 1) {
        skidding = -30;
      } else if (nbReservations === 2) {
        skidding = -10;
      }

      setPopupOffset([skidding, distance]);
    }
  }, [reservations.length]);

  // Background des réservations
  const background = partiel
    ? 'repeating-linear-gradient(45deg, #004a99, #004a99ba 10px, #ffffff 10px, #ffffff 20px)'
    : '#004a99';

  return (
    <Event
      {...rest}
      inverted
      style={{
        ...rest.style,
        background,
        height: '100%', // Pour que la réservation prenne toute la hauteur du créneau
      }}
      onClick={handleEventClick}
    >
      <PopupListView
        trigger={(<div ref={eventRef} onMouseEnter={handleMouseEnter} style={{ height: '100%', width: '100%' }} />)}
        mouseEnterDelay={500}
        hoverable
        flowing
        onClick={handlePopupClick}
        position={popupPosition}
        offset={popupOffset}
        content={(
          <ListView
            permanentView
            viewWidth={400}
            items={_.map(reservations, (reservation) => {
              const {
                identifiant,
                dateDebut,
                dateFin,
                salleIndisponible = false,
                complementInformation,
                identifiantReservant,
                identifiantTypeReservation,
                identifiantStatuts,
              } = reservation;

              const statut = _.find(statuts, { identifiant: identifiantStatuts });
              const type = _.find(typesReservation, { identifiant: identifiantTypeReservation });
              const reservant = _.find(reservants, { identifiant: identifiantReservant });

              const {
                nomClassement,
                typeReservant,
              } = reservant || {};

              const {
                libelle: libelleStatut,
                couleurFond: couleurStatut,
              } = statut || {};

              const {
                libelle: libelleTypeReservation,
                couleurPlanning: couleurTypeReservation,
              } = type || {};

              const startDate = dateDebut.format('D MMM');
              const startHour = dateDebut.format('HH:mm');
              const endDate = dateFin.format('D MMM');
              const endHour = dateFin.format('HH:mm');

              // Si la réservation est une maintenance alors on a pas de nom de réservant
              const libelleReservant = salleIndisponible
                ? 'Indisponible'
                : `Réservation faite par ${nomClassement}`;

              const color = getReservationColor(
                typeCouleur,
                couleurParticulier,
                couleurStatut,
                couleurTypeReservation,
                typeReservant,
              );

              const tooltip = getReservationTooltipText(
                typeCouleur,
                typeReservant,
                libelleStatut,
                libelleTypeReservation,
              );

              const title = (
                <a
                  style={{ cursor: 'pointer', color: COULEUR_BLEU_JVS }}
                  onClick={(e) => handleRedirect(e, reservation)}
                >
                  🔗
                  {' '}
                  {libelleReservant}
                </a>
              );

              return ({
                identifiant,
                title: (
                  <a
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => handleRedirect(e, reservation)}
                  >
                    {salleIndisponible && (
                      <Icon
                        name="warning sign"
                        size="small"
                        style={{ paddingRight: 5, marginTop: -2 }}
                        title="En maintenance"
                      />
                    )}
                    {salleIndisponible
                      ? 'Maintenance'
                      : (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          alignContent: 'center',
                        }}
                        >
                          {!_.isEmpty(color) && (
                            <div
                              title={tooltip}
                              style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: color,
                                marginRight: '5px',
                              }}
                            />
                          )}
                          {nomClassement}
                        </div>
                      )}
                  </a>
                ),
                subtitle: _.isNil(dateDebut)
                  ? ''
                  : (
                    <a
                      style={{ cursor: 'pointer', color: 'black' }}
                      onClick={(e) => handleRedirect(e, reservation)}
                    >
                      Du
                      {' '}
                      {startDate}
                      {' '}
                      à
                      {' '}
                      {_.replace(startHour, ':', 'h')}
                      {' '}
                      au
                      {' '}
                      {endDate}
                      {' '}
                      à
                      {' '}
                      {_.replace(endHour, ':', 'h')}
                    </a>
                  ),
                view: {
                  title,
                  descriptionTitle: 'Observation :',
                  description: complementInformation,
                  footer: (
                    <ListViewPictos
                      statut={_.upperFirst(statut?.libelle)}
                      type={_.upperFirst(type?.libelle)}
                      maintenance={salleIndisponible}
                    />
                  ),
                },
              });
            })}
            action={(
              <ListViewAction
                color="blue"
                style={{
                  borderTopLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  borderTopRightRadius: 0,
                }}
                size="tiny"
                icon="add to calendar"
                content="Ajouter une réservation"
                onClick={handleCreate}
              />
            )}
          />
        )}
      />
    </Event>
  );
};

Creneau.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  dateDebut: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  reservations: PropTypes.array,
  partiel: PropTypes.bool,
  onClick: PropTypes.func,
};
Creneau.defaultProps = {
  dateDebut: null,
  reservations: [],
  partiel: false,
  onClick: null,
};

export default Creneau;
